import { Box2, Vector2 } from "three";
import { OnAnimationFrame } from "../../../animation/OnAnimationFrame";
import { HSLColor } from "../../../colors/HSLColor";
import { HTMLColor } from "../../../colors/HTMLColor";
import { ElementType } from "../../../dom-tools/ElementType";
import { PixelSpace } from "../../../dom-ui/basics/PixelSpace";
import { QuantizationHelper } from "../../../dom-ui/basics/QuantizationHelper";
import { ComparableUIProperty, UIProperty } from "../../../dom-ui/basics/UIProperty";
import { Canvas2dBase } from "../../../dom-ui/graphical/Canvas2dBase";
import { MathX } from "../../../math/MathX";
import { AudioMath } from "../../AudioMath";


export class ComposerGrid extends Canvas2dBase
{
  static readonly elementType = new ElementType( "composer--grid" );

  readonly min  = new ComparableUIProperty( this, new Vector2( 0, 0 ) );
  readonly max  = new ComparableUIProperty( this, new Vector2( 10, 10 ) );
  readonly lineSpacing = new ComparableUIProperty( this, new Vector2( 1, 1 ) );

  readonly lineWidth = new UIProperty( this, 1 );
  readonly backgroundColor = new ComparableUIProperty( this, new HSLColor( 220, 20, 15 ) );
  readonly color = new ComparableUIProperty( this, new HSLColor( 0, 0, 0 ) );

  #pixelSpace = new PixelSpace();
  #onAnimationFrame = new OnAnimationFrame();

  constructor()
  {
    super( ComposerGrid.elementType.create() );

    this._linkRedraw( 
      this.min,
      this.max,
      this.lineSpacing,
      this.lineWidth,
      this.backgroundColor,
      this.color
    );

    this.#onAnimationFrame.addListener(
      ()=>
      { 
        if ( ! this.#paintFlag )
        {
          return;
        }

        this.#paintCanvas();
      }
    )
    
    this.#onAnimationFrame.run();
  } 

  _update()
  {
    this.#paintFlag = true;
  }

  #paintFlag = false;
  #paintCanvas()
  {
    
    this.#pixelSpace.set( this.min.value, this.max.value, this.size );

    this.fillBackground( this.backgroundColor.value );

    this._drawBackground();


    this.setLineStyle( this.color.value, this.lineWidth.value );    
    
    let ranges = QuantizationHelper.getRanges( this.min.value, this.max.value, this.lineSpacing.value );    

    this.context.beginPath();

    let buffer = new Vector2();

    for ( let i = 0; i < ranges.num.x; i++ )
    {
      let value = ranges.start.x + i * this.lineSpacing.value.x;

      this.#pixelSpace.compute( value, this.min.value.y, buffer );      
      this.context.moveTo( buffer.x, buffer.y );

      this.#pixelSpace.compute( value, this.max.value.y, buffer ); 
      this.context.lineTo( buffer.x, buffer.y );
    }

    this.context.stroke();

    this.context.beginPath();

    for ( let i = 0; i < ranges.num.y; i++ )
    {
      let value = ranges.start.y + i * this.lineSpacing.value.y;

      this.#pixelSpace.compute( this.min.value.x, value, buffer );      
      this.context.moveTo( buffer.x, buffer.y );

      this.#pixelSpace.compute( this.max.value.x, value, buffer ); 
      this.context.lineTo( buffer.x, buffer.y );
    }

    this.context.stroke();

    this.#paintFlag = false;

  } 

  _drawBackground()
  {
    let blockSpacing = new Vector2( 4, 12 ); 
    let ranges = QuantizationHelper.getRanges( this.min.value, this.max.value, blockSpacing );

    let start = new Vector2();
    let end   = new Vector2();

    for ( let y = 0; y < ranges.num.y; y++ )
    {
      start.y = ranges.getY( y );
      end.y   = ranges.getY( y + 1 );

      for ( let x = 0; x < ranges.num.x; x++ )
      {
        start.x = ranges.getX( x );
        end.x   = ranges.getX( x + 1 );

        let center = ( start.x + end.x ) / 2;;
        let brighter = ranges.isInPatternX( center, 2, 0 );
        this._drawNotesBlock( start, end, brighter );
      }
    }

    
  } 

  #lightenStep = 2;

  _drawNotesBlock( min:Vector2, max:Vector2, brighter:boolean )
  {
    if ( ! this.#pixelSpace.intersects( min, max ) )
    {
      return;
    }

    let lightenStep = this.#lightenStep;

    let color:HTMLColor = this.backgroundColor.value;
    let brightColor = color.clone().lighten( lightenStep );
    let cColor = brightColor.clone().lighten( lightenStep * 2 );

    if ( brighter )
    {
      color = color.clone().lighten( lightenStep );
      brightColor.lighten( lightenStep );
      cColor = cColor.lighten( lightenStep );
    }


    let noteMin = new Vector2();
    let noteMax = new Vector2();

    for ( let i = 0; i < 12; i++ )
    {
      let x0 = min.x;
      let x1 = max.x;
      let y0 = MathX.lerp( min.y, max.y, i / 12.0 );
      let y1 = MathX.lerp( min.y, max.y, ( i + 1 ) / 12.0 );

      let noteIndex = 11 - i;
      let isSharp = AudioMath.isSharpNote( noteIndex );

      noteMin.set( x0, y0 );
      noteMax.set( x1, y1 );

      this._drawRect( noteMin, noteMax, noteIndex === 0 ? cColor : isSharp ? color : brightColor );
    }
    
  }


  _rectStart:Vector2 = new Vector2();
  _rectEnd:Vector2 = new Vector2();

  _drawRect( min:Vector2, max:Vector2, color:HTMLColor )
  {
    if ( ! this.#pixelSpace.intersects( min, max ) )
    {
      return;
    }

    this.context.fillStyle = color + "";
    
    this.#pixelSpace.compute( min.x, min.y, this._rectStart );
    this.#pixelSpace.compute( max.x, max.y, this._rectEnd );
    
    this._rectEnd.sub( this._rectStart );

    this.context.fillRect( this._rectStart.x, this._rectStart.y, this._rectEnd.x, this._rectEnd.y );
  }

}